import { useEffect, useRef } from 'react'

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

// One shared scroll-lock counter so stacked dialogs don't unlock the page when
// the top one closes (globals.css `body.modal-open` does the actual locking).
let lockCount = 0

/**
 * The one modal accessibility contract (DES-08): focus moves into the dialog
 * on open, Tab is trapped inside it, Escape closes, body scroll locks, and
 * focus returns to the opener on close. Extracted from ContactDrawer's
 * open/close handling and applied to every dialog/overlay.
 *
 * Usage: `const dialogRef = useModalA11y<HTMLDivElement>(open, onClose)` and
 * spread onto the dialog element:
 *   <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
 *
 * `options.returnFocus` names a stand-in for an opener that is gone by the
 * time the dialog closes (the Help peek tab retires once its video is
 * watched), so focus doesn't fall to <body>.
 */
export function useModalA11y<T extends HTMLElement = HTMLDivElement>(
  open: boolean,
  onClose: () => void,
  options?: { returnFocus?: () => HTMLElement | null },
) {
  const dialogRef = useRef<T | null>(null)
  const prevFocusRef = useRef<HTMLElement | null>(null)
  // Latest fallback, kept out of the effect's deps so an inline callback
  // doesn't re-run the open/close cycle.
  const returnFocusRef = useRef(options?.returnFocus)
  returnFocusRef.current = options?.returnFocus

  useEffect(() => {
    if (!open) return
    prevFocusRef.current = document.activeElement as HTMLElement | null
    if (++lockCount === 1) document.body.classList.add('modal-open')

    // Initial focus: the first focusable control, else the dialog itself
    // (consumers set tabIndex={-1} so it can take focus).
    const dialog = dialogRef.current
    const focusables = dialog ? Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (item) => item.tabIndex >= 0 && item.offsetParent !== null,
    ) : []
    ;(focusables[0] ?? dialog)?.focus?.()

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      const el = dialogRef.current
      if (!el) return
      const items = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        // Composite controls (e.g. a combobox list) have buttons with -1.
        // The browser skips them when tabbing, so they cannot be trap endpoints.
        (item) => item.tabIndex >= 0 && (item.offsetParent !== null || item === document.activeElement),
      )
      if (items.length === 0) {
        e.preventDefault()
        el.focus()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement as HTMLElement | null
      if (e.shiftKey) {
        if (active === first || !el.contains(active)) {
          e.preventDefault()
          last.focus()
        }
      } else if (active === last || !el.contains(active)) {
        e.preventDefault()
        first.focus()
      }
    }
    // Capture phase so the trap wins over page-level key handlers.
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      if (--lockCount === 0) document.body.classList.remove('modal-open')
      const prev = prevFocusRef.current
      if (prev?.isConnected) prev.focus?.()
      else returnFocusRef.current?.()?.focus()
    }
  }, [open, onClose])

  return dialogRef
}
